import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import {
  parseAdjustPercent,
  parseRevisedCost,
  type RevisedCostAdjustment,
} from './lineBuyTerms';
import { isBuyDesignated, type DesignatedLine } from './lineFilters';
import { isReleasedStatus } from './releasedStatus';

/**
 * The revised cost adjust popover's choices (GEN-3141): what to do and which
 * lines to do it to.
 */
export type AdjustKind = RevisedCostAdjustment['kind'];
export type AdjustScope = 'line' | 'selected' | 'order';

/** The adjustment a typed value makes, or null when the value is not valid. */
export function adjustmentFrom(
  kind: AdjustKind,
  text: string,
): RevisedCostAdjustment | null {
  if (kind === 'reset') return { kind };
  if (kind === 'set') {
    const unitCost = parseRevisedCost(text);
    // Revised cost is stored at four decimals; reject more than that rather
    // than letting the database round it silently.
    if (unitCost == null || Number(unitCost.toFixed(4)) !== unitCost) {
      return null;
    }
    return { kind, unitCost };
  }
  const percent = parseAdjustPercent(text, kind);
  return percent === undefined ? null : { kind, percent };
}

type AdjustableCandidate = DesignatedLine &
  Pick<AssignmentLine, 'suggestionId' | 'status'>;

/**
 * A line whose revised cost can still change: a buy that is not released
 * (a released line's terms are already on its order).
 */
export function isAdjustableLine(line: AdjustableCandidate): boolean {
  return isBuyDesignated(line) && !isReleasedStatus(line.status);
}

/**
 * The lines a scope covers: the line, the shift-highlight, or the order.
 * Released lines and lines that buy nothing are left out of a group.
 */
export function linesInScope<Line extends AdjustableCandidate>(
  scope: AdjustScope,
  line: Line,
  selectedIds: readonly string[],
  orderLines: readonly Line[],
): Line[] {
  if (scope === 'line') return [line];
  if (scope === 'order') return orderLines.filter(isAdjustableLine);
  const ids = new Set(selectedIds);
  return orderLines.filter(
    (l) => ids.has(l.suggestionId) && isAdjustableLine(l),
  );
}

export const ADJUST_KINDS: readonly AdjustKind[] = [
  'decrease',
  'increase',
  'set',
  'reset',
];
const ADJUST_SCOPES: readonly AdjustScope[] = ['line', 'selected', 'order'];

export function isAdjustKind(value: string): value is AdjustKind {
  return (ADJUST_KINDS as readonly string[]).includes(value);
}

export function isAdjustScope(value: string): value is AdjustScope {
  return (ADJUST_SCOPES as readonly string[]).includes(value);
}
